import React, { useEffect, useMemo, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { IoChevronBackOutline } from 'react-icons/io5';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Body, BodyColor, BodyType, Button, ButtonTheme, ButtonVariant, Heading, InfoPanel, ProgressBar, SpinnerLite, TypographyColor, TypographyType } from '../../ui-elements'
import * as Constants from '../../utils/constants/Constants'
import { checkboxState, compareListState, geoJsonState, geoState, loggedUserState, markerCarouselState, POICategories, ptypeState, selectedCategoryState, selectedGeoNameState, selectedStoryState, selectedTabState, selPOICategories, shimmerLiteState, showDrawerState, spinnerLiteState, spinnerState, storiesState, userCurrencyState } from '../../states';
import { useCIFService, useDashboardService } from '../../services';
import { getKpiBgColor, getParentGeoId, isAddedToCompare, useHandleError } from '../../helpers';
import { AddToCompare, DeckGLMapContainer, MapContainer } from '../home';
import { CatchmentMap } from '../catchments';
import { RouteConstants } from '../../constants';
import { poiVisibleState, selectedCardState, kpiValuesLoadedState } from '../../states/DashboardState';


interface GlobalLeftSidebarProps {
    getSidebarProps: { route: string, backButtonText: string };
}
export const GlobalLeftSidebar = ({ getSidebarProps: { route, backButtonText } }: GlobalLeftSidebarProps) => {
    const API_KEY: any = process.env.REACT_APP_GOOGLE_API_KEY;
    const SHOW_DECKGL_LAYER = process.env.REACT_APP_SHOW_DECKGL_LAYER === 'true';
    const navigate = useNavigate();
    const { getErrorMsg } = useHandleError();
    const [searchParams, setSearchParams] = useSearchParams();
    const cifService = useCIFService();
    const dashboardService = useDashboardService();
    const isChecked = useRecoilValue(checkboxState);
    const selectedGeo = useRecoilValue(geoState);
    const selCurrency = useRecoilValue(userCurrencyState);
    const poiCategories = useRecoilValue(POICategories);
    const stories = useRecoilValue(storiesState);
    const selectedStory = useRecoilValue(selectedStoryState);
    const selectedGeoName = useRecoilValue(selectedGeoNameState);
    const compareList = useRecoilValue(compareListState);
    const geoJSON = useRecoilValue(geoJsonState);
    const setSelectedCategory = useSetRecoilState(selectedCategoryState);
    const [spinner, setSpinner] = useRecoilState(spinnerState);
    const [spinnerLite, setSpinnerLite] = useRecoilState(spinnerLiteState);
    const [shimmerLite, setShimmerLite] = useRecoilState(shimmerLiteState);
    const [selCategoryList, setSelCategoryList] = useRecoilState(selPOICategories);
    const [kpiValues, setKpiValues] = useState<any>({});
    const [presenceDetailsData, setPresenceDetailsData] = useState<any>({});
    const [poi, setPoi] = useState<any>(null);
    const [mapSpinner, setMapSpinner] = useState<number>(0);
    const [myPresence, setMyPresence] = useState({ info: [], markers: [] });
    const [isCardExpand, setIsCardExpand] = useState({ mbpData: false, EhData: false, cTVData: false, poiData: false });
    const [selectedCardType, setSelectedCardType] = useRecoilState(selectedCardState);
    const [poiVisible, setPoiVisible] = useRecoilState(poiVisibleState);
    const loggedUser = useRecoilValue(loggedUserState);
    const selectedTab = useRecoilValue(selectedTabState);
    const setKpiValuesLoaded = useSetRecoilState(kpiValuesLoadedState);
    const { pathname, search } = useLocation();
    type CardKeys = 'mbpData' | 'EhData' | 'cTVData' | 'poiData';
    const [mbpState, setMbpState] = useState<any>({});
    const catchment = searchParams.get("catchment");
    const pType = useRecoilValue(ptypeState);
    const [mapResetKey, setMapResetKey] = useState<number>(0);
    const setDrawerOpen = useSetRecoilState(showDrawerState);
    const setMarkerCarousel = useSetRecoilState(markerCarouselState);

    const navigateToRoute = () => {
        // Clone the searchParams to avoid mutating the original one directly
        const updatedSearchParams = new URLSearchParams(searchParams);
        if (backButtonText === 'Back to Stories') {
            updatedSearchParams.delete('family_id');
        }
        else {
            // Remove pagination parameters
            ['page_no', 'storiespp', 'sort_by', 'reverse'].forEach(param => updatedSearchParams.delete(param));
        }
        // Close bottom tables when navigating back to the map
        setDrawerOpen(false);
        setMarkerCarousel(null);
        // Set the updated searchParams
        setSearchParams(updatedSearchParams);
        navigate({
            pathname: updatedSearchParams.get('catchment') !== null ? 'catchments' : route,
            search: `?${updatedSearchParams.toString()}`,
        });
    }

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return geo_id === '1' ? 'EW-IN-IND-22-0' : geo_id;
    }, [selectedGeo]);

    // Prepare the payload for getMyPresence query
    const myPresencePayload = useMemo(() => {
        if ((catchment || geoCode) && loggedUser.company && (pathname === RouteConstants.dashboard)) {
            const payload = localStorage.getItem('mbp-filter');
            if (payload) {
                const updated = { ...JSON.parse(payload), geocode: catchment ? catchment : geoCode, adjacency: isChecked?.adjacency };
                return updated;
            }
        }
        return null;
    }, [geoCode, loggedUser.company, isChecked?.adjacency, catchment, pathname, catchment]);

    // TanStack Query implementation for getMyPresence
    const { data: myPresenceData } = useQuery({
        queryKey: ['myPresence', myPresencePayload],
        queryFn: async () => {
            if (!myPresencePayload) return null;

            var oldfilters = JSON.parse(localStorage?.getItem("filters")!);
            const filters_new = (oldfilters || []).map((filter: { [x: string]: any; rangeMin: any; rangeMax: any; min?: any; max?: any; }) => {
                if (filter.rangeMin !== undefined && filter.rangeMax !== undefined && filter.min !== undefined && filter.max !== undefined) {
                    if (filter.rangeMin === filter.min && filter.rangeMax === filter.max) {
                        const { rangeMin, rangeMax, ...rest } = filter;
                        return rest;
                    }
                }
                return filter;
            });

            try {
                const response = await cifService.getMyPresence(myPresencePayload);
                return response;
            } catch (error) {
                getErrorMsg(error);
                return { info: [], markers: [] };
            }
        },
        enabled: !!myPresencePayload,
        staleTime: 5 * 60 * 1000,
    });

    // Update myPresence state when data changes
    useEffect(() => {
        if (myPresenceData !== undefined) {
            setMyPresence(myPresenceData || { info: [], markers: [] });

            // Update localStorage with the latest payload
            if (myPresencePayload) {
                localStorage.setItem('mbp-filter', JSON.stringify(myPresencePayload));
            }
        }
    }, [myPresenceData]);

    const filtercleanData = (data: any) => {
        return {
            ...data,
            data: data.data
                .map((section: any) => {
                    const filteredCardData = section.cardData.filter((card: any) => card.count !== 0);
                    return filteredCardData.length > 0 ? { ...section, cardData: filteredCardData } : null;
                })
                .filter(Boolean) // Removes null sections
        };
    };

    const getCardState = (isDisabled: any) => {
        if (isDisabled === 'notDisabled') {
            return {
                clickable: true,
                expandable: true,
                borderClass: 'border-black',
                opacity: 1,
                pointerEvents: 'auto',
                cursor: 'pointer'
            };
        }
        if (isDisabled === 'partial') {
            return {
                clickable: false,
                expandable: true,
                borderClass: 'border-black-5',
                opacity: 0.7,
                pointerEvents: 'auto',
                cursor: 'not-allowed'
            };
        }
        // complete
        return {
            clickable: false,
            expandable: false,
            borderClass: 'border-black-5',
            opacity: 0.5,
            pointerEvents: 'none',
            cursor: 'not-allowed'
        };
    };

    // Fetching KPI values
    const fetchKpivalues = async (payload: any) => {
        const { parent } = getParentGeoId((geoCode), selectedGeo, selectedTab);

        // Check if catchment is present in searchParams
        const catchmentId = searchParams.get('catchment');

        payload = {
            ...payload,
            parent_id: (parent),
            // geoId: Number(geoCode),
            // If catchment is present, use catchment ID for geoId
            ...(catchmentId && { geoId:(catchmentId) }),
        }
        setSpinnerLite(prev => prev + 1);
        setShimmerLite((prev: string[]) => [...prev, "kpiValues"]);
        setKpiValues({});
        setKpiValuesLoaded(false); // Reset before fetching
        try {
            const response = await dashboardService.getkpivalues(payload);
            if (response) {
                setSpinnerLite(prev => prev - 1);
                if (response.data?.length > 0) {
                    // setKpiValues(filtercleanData(response));
                    setKpiValues(response);
                    // Trigger dashboard APIs after KPI values are loaded
                    setKpiValuesLoaded(true);
                } // Check if response.data is not empty
            }
        } catch (error) {
            setSpinnerLite(prev => prev - 1);
            getErrorMsg(error);
            setKpiValuesLoaded(false); // Don't trigger on error
        } finally {
            setShimmerLite((prev: string[]) => prev.filter(item => item !== "kpiValues"));
        }
    };

    const fetchPresenceDetails = async (geoCode: any) => {
        setSpinnerLite(prev => prev + 1);
        setPresenceDetailsData({});
        try {
            const response = await dashboardService.getPresencedetails(geoCode);
            if (response) {
                setSpinnerLite(prev => prev - 1);
                if (response) {
                    setMbpState(getCardState(response.isDisabled));
                    setPresenceDetailsData(response);
                } // Check if response.data is not empty                
            }
        } catch (error) {
            setSpinnerLite(prev => prev - 1);
            getErrorMsg(error);
        }
    };

    const fetchCifPoiData = (payload: any) => {
        // setSpinner(prev => ({ ...prev, spinners: [...prev.spinners, 'fetchCifPoiData'] }));
        const { parent } = getParentGeoId((geoCode), selectedGeo, selectedTab);
        payload = { ...payload, parentgeoId: parent };
        cifService.getCifPoiData(payload)
            .then(res => {
                setPoi(res)
            })
            .catch(error => console.log(error))
        // .finally(() =>
        //     setSpinner(prev => ({ ...prev, spinners: prev.spinners.filter(s => s !== 'fetchCifPoiData') }))
        // );
    }

    const onCardSelect = (cardType: CardKeys, presenceDetailsData?: any) => {
        // 🔹 if same card is already selected, do nothing
        if (selectedCardType?.[cardType]) {
            return;
        }
        setShimmerLite((prev: string[]) => [...prev, "dataIsLoading"]);
        // If count is "--", set poiVisible to false to show NoPoi component
        // Otherwise set poiVisible to true to show data
        setPoiVisible({
            zeroPoIData: presenceDetailsData?.total_count === 0,
            wipPoIData: presenceDetailsData?.total_count === "--",
            poiUpdatedDate: presenceDetailsData.poiUpdatedDate,
            isPoiData: presenceDetailsData.isPoiData
        }
        )
        setSelectedCardType({
            mbpData: cardType === 'mbpData',
            EhData: cardType === 'EhData',
            poiData: cardType === 'poiData',
            cTVData: cardType === 'cTVData'
        });
    };

    const toggleCardExpand = (e: any, cardType: 'mbpData' | 'EhData' | 'cTVData' | 'poiData') => {
        e.stopPropagation(); // Prevent the click event from bubbling up to the parent div
        e.preventDefault(); // Prevent the default action of the event
        // Toggle the clicked card's expand state
        setIsCardExpand(prevState => ({
            ...prevState,              // Keep existing states
            [cardType]: !prevState[cardType]  // Toggle only the clicked card
        }));
    };

    // Determine which card should be selected by default
    const getDefaultSelectedCardType = (presenceDetailsData: any, kpiValues: any, isCatchmentView: boolean = false) => {
        // If in catchment view, always select POI card
        if (isCatchmentView) {
            return {
                mbpData: false,
                EhData: false,
                cTVData: false,
                poiData: true
            };
        }

        if (kpiValues?.data?.length > 0) {
            const poiItem = kpiValues.data.find((item: any) => item.type === 'poiData');
            if (poiItem) {
                if (poiItem.count !== "--") {
                    // PoI is available and count is not "--"
                    return {
                        mbpData: false,
                        EhData: false,
                        cTVData: false,
                        poiData: true
                    };
                } else {
                    // PoI count is "--", check BP
                    if (presenceDetailsData?.isDisabled === 'notDisabled') {
                        return {
                            mbpData: true,
                            EhData: false,
                            cTVData: false,
                            poiData: false
                        };
                    } else {
                        // Fallback to PoI
                        return {
                            mbpData: false,
                            EhData: false,
                            cTVData: false,
                            poiData: true
                        };
                    }
                }
            }
        }
        // If no PoI item, check BP
        if (presenceDetailsData?.isDisabled === 'notDisabled') {
            return {
                mbpData: true,
                EhData: false,
                cTVData: false,
                poiData: false
            };
        }
        // Fallback: select PoI
        return {
            mbpData: false,
            EhData: false,
            cTVData: false,
            poiData: true
        };
    };

    // useEffect(() => {
    //     // setSelectedCardType({
    //     //     mbpData: true,
    //     //     cTVData: false,
    //     //     poiData: false,
    //     //     EhData: false
    //     // });
    //     if (presenceDetailsData && Object.keys(presenceDetailsData).length > 0 && kpiValues && Object.keys(kpiValues).length > 0) {
    //         setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues));
    //     }
    // }, [presenceDetailsData, kpiValues])

    useEffect(() => {
        // Check if catchment is present
        const catchmentId = searchParams.get('catchment');
        const isCatchmentView = !!catchmentId;

        // Only set default card type if data is available and selectedCardType is not already set
        // When catchment is present, we don't need presenceDetailsData
        const hasRequiredData = catchmentId
            ? (kpiValues && Object.keys(kpiValues).length > 0)
            : (presenceDetailsData && Object.keys(presenceDetailsData).length > 0 && kpiValues && Object.keys(kpiValues).length > 0);

        if (hasRequiredData) {
            // Check if selectedCardType is already initialized (any card is selected)
            const isCardTypeInitialized = Object.values(selectedCardType).some(value => value === true);

            if (!isCardTypeInitialized) {
                // If in catchment view, always select POI card regardless of localStorage
                if (isCatchmentView) {
                    setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, true));
                } else {
                    try {
                        const savedCard = localStorage.getItem("selectedCardType");
                        if (savedCard) {
                            const parsedCard = JSON.parse(savedCard);
                            // Validate that the saved data has the correct structure
                            if (parsedCard && typeof parsedCard === 'object' &&
                                'mbpData' in parsedCard && 'EhData' in parsedCard &&
                                'cTVData' in parsedCard && 'poiData' in parsedCard) {
                                setSelectedCardType(parsedCard);
                            } else {
                                // Invalid saved data, use default
                                localStorage.removeItem("selectedCardType");
                                setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, false));
                            }
                        } else {
                            // No saved data, use default
                            setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, false));
                        }
                    } catch (error) {
                        console.error("Error parsing selectedCardType from localStorage:", error);
                        localStorage.removeItem("selectedCardType");
                        setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, false));
                    }
                }
            }
        }
    }, [presenceDetailsData, kpiValues, selectedCardType, searchParams]);

    useEffect(() => {
        if (geoCode && selCategoryList.category?.length > 0 && selCategoryList.subcategory?.length > 0 && selCurrency) {
            const payload = {
                geoId: geoCode,
                currency: selCurrency,
                category: selCategoryList.category,
                subcategory: selCategoryList.subcategory,
            };
            fetchKpivalues(payload);
            fetchCifPoiData(payload);
        }
    }, [selectedGeo, selCategoryList, selCurrency, geoCode, catchment]);

    useEffect(() => {
        // Don't call fetchPresenceDetails if catchment is present in URL
        if (!catchment) {
            fetchPresenceDetails(geoCode);
        }
    }, [geoCode, catchment]);

    useEffect(() => {
        if (poiCategories?.length === 0 && (window.location.pathname === '/stories' || window.location.pathname === '/story-details')) {
            const savedCategories = localStorage.getItem('selCategoryList');
            if (savedCategories) {
                setSelCategoryList(JSON.parse(savedCategories));
            }
            else {
                cifService.getPOICategoryList();
            }
        }
    }, []);

    useEffect(() => {
        if (poiCategories?.length > 0) {
            if (!selCategoryList?.category?.length) {
                const selCats = {
                    category: poiCategories.map(category => category.key) || [],
                    subcategory: poiCategories
                        .flatMap(category => category.sub_categories?.map(sub => sub.key) || [])
                        .filter((key): key is number => key !== undefined)
                }
                setSelCategoryList(selCats);
                localStorage.setItem('selCategoryList', JSON.stringify(selCats));
            }
            setSelectedCategory(poiCategories[0]); // Set the default to the first category
        }
    }, [poiCategories]);


    // Keep the existing save to localStorage effect:
    useEffect(() => {
        // Only save if selectedCardType has at least one true value (is initialized)
        const isInitialized = Object.values(selectedCardType).some(value => value === true);
        if (isInitialized) {
            localStorage.setItem("selectedCardType", JSON.stringify(selectedCardType));
        }
    }, [selectedCardType]);

    // Alternative approach - you can also add this useEffect at the top of your component
    // to load from localStorage immediately on component mount:
    // useEffect(() => {
    //     try {
    //         const savedCard = localStorage.getItem("selectedCardType");
    //         if (savedCard) {
    //             const parsedCard = JSON.parse(savedCard);
    //             // Validate structure
    //             if (parsedCard && typeof parsedCard === 'object' &&
    //                 'mbpData' in parsedCard && 'EhData' in parsedCard &&
    //                 'cTVData' in parsedCard && 'poiData' in parsedCard) {
    //                 setSelectedCardType(parsedCard);
    //             }
    //         }
    //     } catch (error) {
    //         console.error("Error loading selectedCardType from localStorage:", error);
    //         localStorage.removeItem("selectedCardType");
    //     }
    // }, []); // Empty dependency array - runs only on mount
    useEffect(() => {
        // Check if in catchment view
        const catchmentId = searchParams.get('catchment');
        const isCatchmentView = !!catchmentId;
        if (isCatchmentView) {
            setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, true));
            return;
        }

        if (Object.keys(presenceDetailsData).length > 0 && Object.keys(kpiValues).length > 0) {
            // If in catchment view, always select POI card

            // Retrieve previously saved card type from localStorage
            const savedCard = localStorage.getItem('selectedCardType');
            if (savedCard) {
                try {
                    const parsedCard = JSON.parse(savedCard);
                    // Validate structure
                    if (
                        typeof parsedCard === 'object' &&
                        'mbpData' in parsedCard &&
                        'EhData' in parsedCard &&
                        'cTVData' in parsedCard &&
                        'poiData' in parsedCard
                    ) {
                        // Find which card is marked true

                        const selectedKey = Object.keys(parsedCard).find(key => parsedCard[key] === true);
                        // find the corresponding API object for this card (e.g., for cTVData)
                        let isClickable = false;
                        if (selectedKey === 'mbpData' && presenceDetailsData) {
                            isClickable = getCardState(presenceDetailsData.isDisabled).clickable;
                        } else if (kpiValues?.data?.length > 0) {
                            const cardItem = kpiValues.data.find((item: any) => item.type === selectedKey);
                            if (cardItem) {
                                isClickable = getCardState(cardItem.isDisabled).clickable;
                            }
                        }
                        if (isClickable) {
                            setSelectedCardType(parsedCard);
                            return;
                        }
                    }
                } catch (error) {
                    console.error('Error parsing selectedCardType from localStorage', error);
                }
            }
            // fallback: localStorage invalid or not clickable, use default selector
            setSelectedCardType(getDefaultSelectedCardType(presenceDetailsData, kpiValues, false));
        }
    }, [presenceDetailsData, kpiValues, searchParams]);

    // Update poiVisible based on the currently selected card's count
    useEffect(() => {
        if (Object.keys(kpiValues).length > 0 && Object.keys(selectedCardType).length > 0) {
            // Find which card is currently selected
            const selectedKey = Object.keys(selectedCardType).find(key => selectedCardType[key as keyof typeof selectedCardType] === true);

            if (selectedKey) {

                // For other cards (EhData, cTVData, poiData), check the count from kpiValues
                const cardItem = kpiValues.data?.find((item: any) => item.type === selectedKey);
                if (cardItem) {
                    setPoiVisible({
                        zeroPoIData: cardItem.count === 0,
                        wipPoIData: cardItem.count === "--",
                        poiUpdatedDate: cardItem.poiUpdatedDate,
                        isPoiData: cardItem.isPoiData
                    })
                }

            }
        }
    }, [kpiValues, selectedCardType, presenceDetailsData]);

    // Reset map zoom whenever selectedGeo changes
    useEffect(() => {
        setMapResetKey(prev => prev + 1);
    }, [selectedGeo]);

    return (
        <div className='col-2 h-100 d-flex flex-column justify-content-start align-items-start border-right padding-left-right-5' style={{ padding: '2.8125vh 0' }}>
            <div className='d-flex flex-row justify-content-start align-items-start height-5'>
                <Button
                    theme={ButtonTheme.primary}
                    variant={ButtonVariant.transparent}
                    type='button'
                    onClick={() => navigateToRoute()}
                    classname='padding-0 h-auto text-underline fs-14 button-gray d-flex align-items-center'
                >
                    <IoChevronBackOutline className='fs-14 margin-right-3' />
                    {backButtonText}
                </Button>
            </div>

            <div className='height-95 overflow-auto no-scrollbar w-100 d-flex flex-column justify-content-start gap-2'>
                <div className='border w-100 rounded mini-map-container'>
                    <APIProvider apiKey={API_KEY} libraries={["marker"]} >
                        {searchParams.get('catchment') ?
                            <CatchmentMap
                                catchmentId={(searchParams.get('catchment') || undefined)}
                                myPresenceMarkers={myPresence?.markers}
                            /> :
                            SHOW_DECKGL_LAYER && selectedGeo?.state ?
                                <DeckGLMapContainer
                                    key={mapResetKey}
                                    spinnerLite={mapSpinner}
                                    setMapSpinner={setMapSpinner}
                                    stories={stories?.family}
                                    selectedStory={selectedStory}
                                    myPresence={myPresence?.markers}
                                    catchment={catchment}

                                /> :
                                <MapContainer
                                    key={mapResetKey}
                                    spinnerLite={mapSpinner}
                                    setMapSpinner={setMapSpinner}
                                    stories={stories?.family}
                                    selectedStory={selectedStory}
                                    myPresence={myPresence?.markers}
                                    disableZoom={true}
                                    catchment={catchment}
                                />}
                    </APIProvider>
                </div>

                {<>
                    <div className='w-100 d-flex justify-content-between align-items-center padding-top-3'>
                        <Heading
                            title={geoJSON?.rootProperties?.geoName || selectedGeoName || ''}
                            type={TypographyType.h4}
                            colour={TypographyColor.primary}
                            classname='text-start p-0 padding-right-1'
                        />
                        {((search?.includes('sub_district') || search?.includes('district') || search?.includes('village')) || catchment) &&
                            <AddToCompare
                                isAddedToCompare={isAddedToCompare(catchment ? (catchment) : geoCode, compareList, pType)}
                                geoId={catchment ? (catchment) : geoCode}
                                level={geoJSON?.rootProperties?.geoLevelNumber}
                                parent_id={geoJSON?.rootProperties?.geoType === "catchment" ? null : (selectedGeo.district)}
                                count={(() => {
                                    const geoType = geoJSON?.rootProperties?.geoType;
                                    const hasCatchment = catchment && (catchment);
                                    const compareKey = geoType === "catchment" && pType === "Branch" ? "Branch" :
                                            hasCatchment && pType === "RelationshipManager" ? "RelationshipManager" :
                                                hasCatchment && pType === "Partner" ? "Partner" :
                                                    geoType === "village" ? "villages" :
                                                        geoType === "pincode" ? "pincodes" :
                                                            geoType === "sub_district" ? "subdistricts" :
                                                                "districts";
                                    return compareList?.[compareKey]?.length;
                                })()}
                                showNodeWithoutHover={true}
                                tooltipPosition='bottom'
                            />
                        }

                    </div>
                    {(!selectedGeo.district && !searchParams.get('catchment'))
                        && (poi?.Poi?.districtCoverage?.coverage || poi?.Poi?.districtCoverage?.coverage !== 0)
                        && (poi?.Poi?.districtCoverage?.total || poi?.Poi?.districtCoverage?.total !== 0)
                        && <div className='w-100 padding-bottom-3'>
                            <div className="d-flex justify-content-start align-items-center p-0 padding-bottom-1 m-0">
                                <Heading
                                    title={`${poi?.Poi?.districtCoverage?.coverage ?? 0}/${poi?.Poi?.districtCoverage?.total ?? 0}`}
                                    type={TypographyType.h5}
                                    colour={TypographyColor.primary}
                                    classname='text-start p-0 padding-right-1'
                                />
                                <Body
                                    type={BodyType.p2}
                                    color={BodyColor.secondary}
                                    classname="p-0"
                                >
                                    Districts Covered
                                </Body>
                                <InfoPanel text={Constants.iButtonText.dashboardPage.leftPanel.districtsCovered} classname='icon-color-5 margin-left-2 fs-14' translateX='translateX(-71%)' />
                            </div>
                            <ProgressBar coverage={poi?.Poi?.districtCoverage} classname='bg-poi' />
                        </div>
                    }
                    {shimmerLite.includes("kpiValues")
                        ? <div className="w-100 d-flex flex-column gap-2">
                            {[...Array(4)].map((_, index) => (
                                <div
                                    className='rounded p-3 position-relative height-10vh'
                                >
                                    <div className="d-flex justify-content-center align-items-center shimmer rounded position-absolute" ></div>
                                </div>
                            ))}
                        </div>
                        // spinner.spinners.length === 0 && spinnerLite >= 0 && Object.keys(kpiValues).length === 0 && Object.keys(presenceDetailsData).length === 0
                        //     ? <div className='w-100 height-56-45vh'>
                        //         <SpinnerLite />
                        //     </div>
                        : pathname === RouteConstants.dashboard && Object.keys(kpiValues).length > 0
                            // && Object.keys(presenceDetailsData).length > 0
                            ? <div className="w-100 d-flex flex-column gap-2">
                                {Object.keys(presenceDetailsData).length > 0 && !catchment &&
                                    <div className={`business-presence-card p-3 rounded ${mbpState.borderClass}`}
                                        style={{
                                            backgroundColor: selectedCardType.mbpData ? 'rgb(245 244 244)' : "white",
                                            opacity: mbpState.opacity,
                                            cursor: mbpState.cursor,
                                            pointerEvents: mbpState.pointerEvents
                                        }}
                                        aria-disabled={mbpState.clickable ? false : true}
                                        onClick={() => mbpState.clickable && onCardSelect("mbpData", presenceDetailsData?.total_count)}
                                    >
                                        <div className='d-flex flex-column gap-1 cursor-pointer'>
                                            <div className='d-flex flex-row w-100 justify-content-between'>
                                                <Heading
                                                    title={presenceDetailsData?.total_count}
                                                    type={TypographyType.h4}
                                                    colour={TypographyColor.primary}
                                                />
                                                <span
                                                    className={`material-symbols-outlined fs-18 cursor-pointer fw-bold ${mbpState.expandable ? 'color-black' : 'color-black-5'}`}
                                                    onClick={(e) => toggleCardExpand(e, 'mbpData')}
                                                >
                                                    {(mbpState.expandable && isCardExpand.mbpData) ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                </span>
                                            </div>
                                            <Body
                                                type={BodyType.p2}
                                                color={BodyColor.secondary}
                                                classname="p-0"
                                            >
                                                {presenceDetailsData?.title}
                                            </Body>
                                        </div>
                                        {mbpState.expandable && isCardExpand.mbpData && (
                                            <div className='mt-3 d-flex flex-column gap-2'>
                                                <hr className='m-0' />
                                                {presenceDetailsData?.presences?.map((item: any, index: number) => (
                                                    <div className='d-flex align-items-center gap-2' key={index}>
                                                        <div
                                                            style={{
                                                                backgroundColor: 'white',
                                                                display: 'flex',
                                                                height: '2.376vw',
                                                                minWidth: '2.376vw',
                                                                maxWidth: '2.376vw',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                borderRadius: '50%',
                                                            }}
                                                        >
                                                            <img
                                                                key={index}
                                                                src={item.icon}
                                                                alt=""
                                                                height='50%'
                                                                width='50%'

                                                            />
                                                        </div>
                                                        <div className='d-flex flex-column gap-1'>
                                                            <Heading
                                                                title={item.count}
                                                                type={TypographyType.h5}
                                                                colour={TypographyColor.primary}
                                                            />
                                                            <Body
                                                                type={BodyType.p3}
                                                                color={BodyColor.secondary}
                                                                classname="p-0"
                                                            >
                                                                {item.name}
                                                            </Body>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                }
                                {kpiValues?.data?.map((item: any, itemIndex: number) => {
                                    const cardState = getCardState(item.isDisabled);
                                    return (
                                        <div
                                            className={`rounded p-3 ${cardState.borderClass}`}
                                            style={{
                                                backgroundColor: (item.type === "cTVData" && selectedCardType.cTVData)
                                                    ? "#DEF9DE"
                                                    : (item.type === "poiData" && selectedCardType.poiData)
                                                        ? "#E5EBFE"
                                                        : (item.type === "EhData" && selectedCardType.EhData)
                                                            ? "#FFE0F5"
                                                            : "#FFFFFF",
                                                opacity: cardState.opacity,
                                                cursor: cardState.cursor,
                                                pointerEvents: cardState.pointerEvents as React.CSSProperties['pointerEvents'],
                                            }}
                                            key={`${item.sectionId || item.type}-${itemIndex}`}
                                            aria-disabled={cardState.clickable ? false : true}
                                            onClick={() => cardState.clickable && onCardSelect(item.type, item)}
                                        >
                                            <div className='d-flex flex-column gap-1 cursor-pointer'>
                                                <div className='d-flex flex-row w-100 justify-content-between'>
                                                    <Heading
                                                        title={item?.count}
                                                        type={TypographyType.h4}
                                                        colour={TypographyColor.primary}
                                                    />
                                                    <span
                                                        className={`material-symbols-outlined fs-18 cursor-pointer fw-bold ${cardState.expandable ? 'color-black' : 'color-black-5'}`}
                                                        onClick={(e) => toggleCardExpand(e, item.type)}
                                                    >
                                                        {cardState.expandable && isCardExpand[item.type as keyof typeof isCardExpand] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                    </span>
                                                </div>
                                                <Body
                                                    type={BodyType.p2}
                                                    color={BodyColor.secondary}
                                                    classname="p-0"
                                                >
                                                    {item?.title}
                                                </Body>
                                            </div>
                                            {cardState.expandable && isCardExpand[item.type as keyof typeof isCardExpand]
                                                && (kpiValues?.breakdownType === 'State' || kpiValues?.breakdownType === 'District' || kpiValues?.breakdownType === 'SubDistrict'
                                                    ? item?.cardData?.filter((cardItem: any) => !(cardItem.title.includes("Projected CTV by"))).slice(1).map((cardItem: {
                                                        count: string,
                                                        title: string,
                                                        unit: string,
                                                        rank: string,
                                                        cardId: string,
                                                        icon: string
                                                    }, index: number) => {
                                                        // const IconComponent = MdIcons[cardItem.icon as keyof typeof MdIcons];
                                                        return (
                                                            <div className='d-flex flex-column' key={`${item.icon}-${itemIndex}-${cardItem.cardId}-${index}`}>
                                                                <hr className='m-0 my-3' style={{ borderTop: '1px solid rgba(219, 219, 219, 1)' }} />
                                                                <div
                                                                    className={`d-flex flex-row align-items-center gap-2`}
                                                                    key={`${item.type}-${itemIndex}-${cardItem.cardId}-${index}`}
                                                                >
                                                                    {/* Icon  */}
                                                                    <div className={`d-flex justify-content-center align-items-center rounded-circle ${getKpiBgColor(item.type, selectedCardType[item.type as keyof typeof selectedCardType])}`}
                                                                        style={{
                                                                            height: '2.376vw',
                                                                            minWidth: '2.376vw',
                                                                            maxWidth: '2.376vw',
                                                                            margin: '0 0.99vw 0 0',
                                                                            boxSizing: 'border-box'
                                                                        }}
                                                                    >
                                                                        <span className="material-symbols-outlined fs-23 " style={{ color: item.type === "cTVData" ? "#276628" : item.type === "EhData" ? "#DC28B8" : "#0030C7" }} >{cardItem.icon}</span>
                                                                    </div>
                                                                    {/* Content */}
                                                                    <div className='d-flex flex-column'>
                                                                        <Heading
                                                                            title={cardItem.count && cardItem.unit ? `${cardItem.count} ${cardItem.unit}` : cardItem.count ? `${cardItem.count}` : `${"No data"}`}
                                                                            type={TypographyType.h4}
                                                                            colour={TypographyColor.primary}
                                                                        />
                                                                        <div className='d-flex flex-row align-items-center'>
                                                                            <Body
                                                                                type={BodyType.p2}
                                                                                color={BodyColor.secondary}
                                                                                classname="text-start"
                                                                            >
                                                                                {cardItem.title ? cardItem.title : 'No data'}
                                                                            </Body>
                                                                        </div>
                                                                        {kpiValues?.breakdownType !== 'State' && <Body
                                                                            type={BodyType.p4}
                                                                            color={BodyColor.secondary}
                                                                            classname="text-start"
                                                                        >
                                                                            {cardItem.rank}
                                                                        </Body>}
                                                                    </div>
                                                                </div >
                                                                {/* {((item.cardData.filter((cardItem: any) => !(kpiValues?.breakdownType !== 'State' && cardItem.title.includes("Projected CTV by"))).length - 1) !== index) && <hr className='my-1 mx-2' key={`hr-${cardItem.title}-${cardItem.cardId}-${index}`} />} */}
                                                            </div>
                                                        )
                                                    })
                                                    : item?.cardData?.filter((cardItem: any) => item.type !== "cTVData").slice(1).map((cardItem: {
                                                        count: string,
                                                        title: string,
                                                        unit: string,
                                                        rank: string,
                                                        cardId: string,
                                                        icon: string
                                                    }, index: number) => {
                                                        // const IconComponent = MdIcons[cardItem.icon as keyof typeof MdIcons];
                                                        return (
                                                            <div className='d-flex flex-column' key={`${item.icon}-${itemIndex}-${cardItem.cardId}-${index}`}>
                                                                <hr className='m-0 my-3' style={{ borderTop: '1px solid rgba(219, 219, 219, 1)' }} />
                                                                <div
                                                                    className="d-flex flex-row align-items-center gap-2"
                                                                    key={`${item.type}-${itemIndex}-${cardItem.cardId}-${index}`}
                                                                >
                                                                    {/* Icon  */}
                                                                    <div
                                                                        style={{
                                                                            backgroundColor: item.type === "EhData"
                                                                                ? "#DEF9DE"
                                                                                : (item.type === "cTVData" && isCardExpand.cTVData)
                                                                                    ? "#DEF9DE"
                                                                                    : (item.type === "poiData" && isCardExpand.cTVData)
                                                                                        ? "#E5EBFE"
                                                                                        : "#FFFFFF",
                                                                            display: 'flex',
                                                                            height: '2.376vw',
                                                                            minWidth: '2.376vw',
                                                                            maxWidth: '2.376vw',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center',
                                                                            borderRadius: '50%',
                                                                            margin: '0 0.99vw',
                                                                        }}
                                                                        className='margin-top-1'
                                                                    >
                                                                        <span className="material-symbols-outlined fs-23 " style={{ color: item.type === "EhData" || item.type === "cTVData" ? "#276628" : "#0030C7" }} >{cardItem.icon}</span>
                                                                    </div>
                                                                    {/* Content */}
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <Heading
                                                                            title={cardItem.count && cardItem.unit ? `${cardItem.count} ${cardItem.unit}` : cardItem.count ? `${cardItem.count}` : `${"No data"}`}
                                                                            type={TypographyType.h4}
                                                                            colour={TypographyColor.primary}
                                                                        />
                                                                        <Body
                                                                            type={BodyType.p2}
                                                                            color={BodyColor.secondary}
                                                                            classname="text-start"
                                                                        >
                                                                            {cardItem.title ? cardItem.title : 'No data'}
                                                                        </Body>
                                                                        {/* <InfoPanel text={cardItem.title ? cardItem.title : 'info'} classname='icon-color-5 margin-left-2 fs-14' /> */}
                                                                        {kpiValues?.breakdownType !== 'State' && <Body
                                                                            type={BodyType.p4}
                                                                            color={BodyColor.secondary}
                                                                            classname="text-start"
                                                                        >
                                                                            {cardItem.rank}
                                                                        </Body>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                )}
                                        </div>
                                    )
                                })}
                            </div>
                            : null
                    }
                </>}
            </div>
        </div>
    )
}

